import { Box, Button, Grid, Skeleton, Typography } from "@mui/material";
import { Candidate, MunicipalCandidate, Precinct, ProvincialCandidate, Tabulation, User } from "../../types";
import { collection, doc, getDoc, getDocs, orderBy, query, where } from "firebase/firestore";
import { useEffect, useState } from "react";

import CandidateComparison from "./CandidateComparison";
import PrecinctVotes from "./PrecinctVotes";
import ProjectionDialog from "./ProjectionDialog";
import { db } from "../../firebase";
import useAuth from "../../hooks/useAuth";

interface BarangayStatsProps {
  region: string;
  province: string;
  district: string;
  municipal: string;
  barangay: string;
  position: string;
}

const BarangayStats: React.FC<BarangayStatsProps> = ({ region, province, district, municipal, barangay, position }) => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [tabulation, setTabulation] = useState<Tabulation>();
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [precincts, setPrecincts] = useState<Precinct[]>([]);
  const [isProjectionDialogOpen, setIsProjectionDialogOpen] = useState(false);

  const fetchBarangayTabulation = async (user: User) => {
    const docSnap = await getDoc(doc(db, `tabulationsV2/${user.dashboard}/regions/${region}/provinces/${province}/districts/${district}/cities/${municipal}/barangays/${barangay}`));
    setTabulation(docSnap.data() as Tabulation);

    const precinctsColRef = collection(db, "precincts");
    const q = query(precinctsColRef,
      where("province", "==", province),
      where("city_municipality", "==", municipal),
      where("barangay", "==", barangay)
    );
    const precinctDocsSnap = await getDocs(q);
    const _precincts = precinctDocsSnap.docs.map((d) => d.data() as Precinct);
    _precincts.sort((a, b) => a.precinct.localeCompare(b.precinct));
    setPrecincts(_precincts);
  };

  const fetchProvincialCandidates = async (): Promise<ProvincialCandidate[]> => {
    const candidatesRef = collection(db, "candidates");
    const q = query(candidatesRef, 
      where("type", "==", "provincial"),
      where("province", "==", province),
      where("position", "==", position),
      orderBy("name", "asc")
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => doc.data() as ProvincialCandidate);
  };  

  const fetchMunicipalCandidates = async (): Promise<MunicipalCandidate[]> => {
    const candidatesRef = collection(db, "candidates");
    const q = query(candidatesRef, 
      where("type", "==", "municipal"),
      where("province", "==", province),
      where("city_municipality", "==", municipal),
      where("position", "==", position),
      orderBy("name", "asc")
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => doc.data() as MunicipalCandidate);
  }

  const fetchTabulationAndCandidates = async (user: User) => {
    await fetchBarangayTabulation(user);
    
    const provincialCandidates = await fetchProvincialCandidates();
    const municipalCandidates = await fetchMunicipalCandidates();
    setCandidates([...provincialCandidates, ...municipalCandidates]);
  }

  useEffect(() => {
    if (user && province && municipal && barangay) {
      setIsLoading(true);
      fetchTabulationAndCandidates(user).then(() => setIsLoading(false));
    }
  }, [user, province, municipal, barangay]);
  
  return (
    <Box sx={{ flexGrow: 1, p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4" gutterBottom sx={{ m: 0 }}>
          {position} (Barangay Level)
        </Typography>
        {tabulation && (
          <Button 
            variant="contained" 
            color="primary" 
            onClick={() => setIsProjectionDialogOpen(true)}
          >
            Projection
          </Button>
        )}
      </Box>
      {isLoading ? (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Skeleton variant="rectangular" width="100%" height={200} />
          </Grid>
          {[1, 2, 3].map((item) => (
            <Grid item xs={12} key={item}>
              <Skeleton variant="rectangular" width="100%" height={150} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {tabulation && (
              <CandidateComparison
                tabulation={tabulation}
                title={`${barangay}, ${municipal}, ${province}`}
                position={position}
                candidates={candidates}
              />
            )}
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ margin: 2 }}>
              <Typography variant="body1">
                Voter voted straight for Party.
              </Typography>
              <Typography variant="body1" sx={{ color: '#9c27b0' }}>
                Voter did not vote for all positions
              </Typography>
              <Typography variant="body1" sx={{ color: '#3f51b5' }}>
                Voter voted for a mix of Abante and Padajon candidates
              </Typography>
              <Typography variant="body1" sx={{ backgroundColor: '#16A34A33', padding: 1 }}>
                Voted candidate is a member of Abante
              </Typography>
              <Typography variant="body1" sx={{ backgroundColor: '#FF000033', padding: 1 }}>
                Voted candidate is a member of Padajon
              </Typography>
            </Box>
          </Grid>            
          {precincts.map((precinct) => (
            <Grid item xs={12} key={precinct.precinct}>
              <PrecinctVotes
                key={precinct.precinct}
                precinct={precinct}
                position={position}
                candidates={candidates}
              />
            </Grid>
          ))}
        </Grid>
      )}
      
      {tabulation && (
        <ProjectionDialog 
          open={isProjectionDialogOpen} 
          region={region}
          province={province}
          district={district}
          municipal={municipal}
          barangay={barangay}
          position={position}
          candidates={candidates}
          tabulation={tabulation}
          onClose={() => setIsProjectionDialogOpen(false)}
        />
      )}
    </Box>
  );
};

export default BarangayStats;
